import { type FormEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { AclAutoLogo, Icon } from './AclAutoDemo.js';
import {
  ACCOUNT_TIMEZONES,
  BILLING_INVOICES,
  CLIENT_LIFECYCLE_SCREENS,
  NOTIFICATION_PREFERENCES,
  ONBOARDING_STEPS,
  PROFILE_MENU_SCREENS,
  REVIEW_TIMELINE,
  US_STATES,
  type ClientLifecycleScreenId,
} from './clientLifecycleData.js';
import './clientLifecycle.css';

export {
  CLIENT_LIFECYCLE_SCREEN_IDS,
  CLIENT_LIFECYCLE_SCREENS,
  type ClientLifecycleScreenId,
  type ClientLifecycleScreenMeta,
} from './clientLifecycleData.js';

export interface ClientLifecycleScreensProps {
  screen: ClientLifecycleScreenId;
  onNavigate: (screen: ClientLifecycleScreenId) => void;
}

type NoticeTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

const delay = (milliseconds: number) =>
  new Promise((resolve) => window.setTimeout(resolve, milliseconds));

function StatusBadge({ children, tone = 'neutral' }: { children: ReactNode; tone?: NoticeTone }) {
  return (
    <span className="cl-status" data-tone={tone}>
      <i />
      {children}
    </span>
  );
}

function ScreenHeading({
  eyebrow,
  title,
  copy,
  action,
}: {
  eyebrow?: string;
  title: string;
  copy: string;
  action?: ReactNode;
}) {
  return (
    <header className="cl-page-heading">
      <div>
        {eyebrow && <span className="cl-eyebrow">{eyebrow}</span>}
        <h1>{title}</h1>
        <p>{copy}</p>
      </div>
      {action}
    </header>
  );
}

function Field({
  label,
  error,
  hint,
  className = '',
  children,
}: {
  label: string;
  error?: string;
  hint?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={`cl-field ${className}${error ? ' has-error' : ''}`}>
      <span>{label}</span>
      {children}
      {error ? (
        <small className="cl-field-error">{error}</small>
      ) : hint ? (
        <small>{hint}</small>
      ) : null}
    </label>
  );
}

function Toggle({
  checked,
  onChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className={`cl-toggle${checked ? ' checked' : ''}`}
      onClick={() => onChange(!checked)}
    >
      <span />
    </button>
  );
}

function InlineNotice({
  tone,
  title,
  children,
}: {
  tone: NoticeTone;
  title: string;
  children: ReactNode;
}) {
  return (
    <div
      className="cl-inline-notice"
      data-tone={tone}
      role={tone === 'danger' ? 'alert' : 'status'}
    >
      <span>
        <Icon
          name={
            tone === 'success'
              ? 'check'
              : tone === 'warning' || tone === 'danger'
                ? 'shield'
                : 'clock'
          }
          size={15}
        />
      </span>
      <div>
        <strong>{title}</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

function ReviewDialog({
  open,
  title,
  description,
  tone = 'info',
  onClose,
  children,
  footer,
}: {
  open: boolean;
  title: string;
  description: string;
  tone?: NoticeTone;
  onClose: () => void;
  children?: ReactNode;
  footer: ReactNode;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const dialog = dialogRef.current;
    const previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (!dialog) return undefined;
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusable = () =>
      Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => element.offsetWidth > 0 || element.offsetHeight > 0);
    const frame = window.requestAnimationFrame(() => (focusable()[0] ?? dialog).focus());
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const elements = focusable();
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (!first || !last) {
        event.preventDefault();
        dialog.focus();
      } else if (
        event.shiftKey &&
        (document.activeElement === first || !dialog.contains(document.activeElement))
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === last || !dialog.contains(document.activeElement))
      ) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('keydown', keydown);
      document.body.style.overflow = oldOverflow;
      if (previousFocus?.isConnected && !previousFocus.closest('[hidden]')) previousFocus.focus();
    };
  }, [open]);

  if (!open) return null;
  return (
    <div className="cl-dialog-layer">
      <button
        type="button"
        className="cl-dialog-backdrop"
        aria-label="Close dialog"
        onClick={onClose}
      />
      <div
        ref={dialogRef}
        className="cl-dialog"
        data-tone={tone}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cl-dialog-title"
        tabIndex={-1}
      >
        <header>
          <span>
            <Icon
              name={tone === 'danger' ? 'shield' : tone === 'success' ? 'check' : 'clock'}
              size={17}
            />
          </span>
          <div>
            <h2 id="cl-dialog-title">{title}</h2>
            <p>{description}</p>
          </div>
          <button
            type="button"
            className="cl-icon-button"
            aria-label="Close dialog"
            title="Close"
            onClick={onClose}
          >
            x
          </button>
        </header>
        {children && <div className="cl-dialog-body">{children}</div>}
        <footer>{footer}</footer>
      </div>
    </div>
  );
}

function ProfileMenu({
  current,
  onNavigate,
}: {
  current: ClientLifecycleScreenId;
  onNavigate: (screen: ClientLifecycleScreenId) => void;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const outside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  const labels: Record<(typeof PROFILE_MENU_SCREENS)[number], string> = {
    account: 'Account',
    billing: 'Billing',
    notifications: 'Notifications',
    support: 'Support',
    signout: 'Sign out',
  };
  return (
    <div className="cl-profile" ref={menuRef}>
      <button
        type="button"
        className="cl-profile-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span>DB</span>
        <span>
          <strong>Dana Brooks</strong>
          <small>Northstar Coverage</small>
        </span>
        <Icon name="chevron" size={13} />
      </button>
      {open && (
        <div className="cl-profile-menu" role="menu">
          {PROFILE_MENU_SCREENS.map((id) => (
            <button
              key={id}
              type="button"
              role="menuitem"
              className={`${current === id ? 'active ' : ''}${id === 'signout' ? 'danger' : ''}`}
              onClick={() => {
                setOpen(false);
                onNavigate(id);
              }}
            >
              {labels[id]}
              <Icon name="chevron" size={12} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LifecycleShell({
  screen,
  onNavigate,
  children,
  notice,
}: {
  screen: ClientLifecycleScreenId;
  onNavigate: (screen: ClientLifecycleScreenId) => void;
  children: ReactNode;
  notice?: ReactNode;
}) {
  const metadata =
    CLIENT_LIFECYCLE_SCREENS.find((item) => item.id === screen) ?? CLIENT_LIFECYCLE_SCREENS[0];
  return (
    <div className="cl-root">
      <nav className="cl-topbar" aria-label="Client workspace">
        <button
          type="button"
          className="cl-brand-button"
          aria-label="Open client demo"
          onClick={() => onNavigate('demo-activation')}
        >
          <AclAutoLogo />
        </button>
        {metadata.authenticated ? (
          <ProfileMenu current={screen} onNavigate={onNavigate} />
        ) : (
          <button
            type="button"
            className="cl-secondary-button"
            onClick={() => onNavigate('demo-activation')}
          >
            View demo
          </button>
        )}
      </nav>
      {notice}
      <main className="cl-main">{children}</main>
      <footer className="cl-footer">
        <span>AtCostLeads.com</span>
        <span>Managed AI lead engagement</span>
      </footer>
    </div>
  );
}

function SignupScreen({
  onNavigate,
  notify,
}: {
  onNavigate: (screen: ClientLifecycleScreenId) => void;
  notify: (message: string) => void;
}) {
  const [form, setForm] = useState({
    name: '',
    company: '',
    email: '',
    mobile: '',
    password: '',
    consent: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'form' | 'loading' | 'verification'>('form');
  const [serviceError, setServiceError] = useState('');
  const update = (key: keyof typeof form, value: string | boolean) =>
    setForm((current) => ({ ...current, [key]: value }));
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = 'Enter your full name.';
    if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = 'Enter a valid work email.';
    if (form.mobile.replace(/\D/g, '').length < 10) next.mobile = 'Enter a 10-digit mobile number.';
    if (form.password.length < 8) next.password = 'Use at least 8 characters.';
    if (!form.consent) next.consent = 'Accept the terms to continue.';
    setErrors(next);
    setServiceError('');
    if (Object.keys(next).length > 0) return;
    setStatus('loading');
    await delay(650);
    if (form.email.endsWith('@example.invalid')) {
      setStatus('form');
      setServiceError('We could not create this account. Check the email and try again.');
      return;
    }
    setStatus('verification');
  };

  if (status === 'verification')
    return (
      <section className="cl-auth-layout">
        <div className="cl-auth-panel cl-verification-panel">
          <span className="cl-success-mark">
            <Icon name="send" size={22} />
          </span>
          <span className="cl-eyebrow">Verify your email</span>
          <h1>Check your inbox</h1>
          <p>
            We sent a verification link to <strong>{form.email}</strong>. Your sample workspace is
            ready after verification.
          </p>
          <div className="cl-auth-actions">
            <button
              type="button"
              className="cl-primary-button"
              onClick={() => onNavigate('demo-activation')}
            >
              Open verified workspace
            </button>
            <button
              type="button"
              className="cl-secondary-button"
              onClick={() => notify('A new verification email was sent.')}
            >
              Resend email
            </button>
          </div>
          <button type="button" className="cl-text-button" onClick={() => setStatus('form')}>
            Use a different email
          </button>
        </div>
      </section>
    );

  return (
    <section className="cl-auth-layout">
      <div className="cl-auth-intro">
        <span className="cl-eyebrow">Create your workspace</span>
        <h1>See your managed AI lead operation come to life.</h1>
        <p>
          Start with a synthetic workspace. Add your real account details only when you are ready to
          begin the buildout.
        </p>
        <dl>
          <div>
            <dt>1</dt>
            <dd>
              <strong>Create your account</strong>
              <span>Use your name and work email.</span>
            </dd>
          </div>
          <div>
            <dt>2</dt>
            <dd>
              <strong>Explore the sample theater</strong>
              <span>See calls, messages, bookings, and transfers.</span>
            </dd>
          </div>
          <div>
            <dt>3</dt>
            <dd>
              <strong>Activate when ready</strong>
              <span>Complete payment and account setup.</span>
            </dd>
          </div>
        </dl>
      </div>
      <div className="cl-auth-panel">
        <span className="cl-eyebrow">Account details</span>
        <h2>Get started</h2>
        <p>Company is optional for independent agents.</p>
        {serviceError && (
          <InlineNotice tone="danger" title="Account not created">
            {serviceError}
          </InlineNotice>
        )}
        <form onSubmit={submit} noValidate>
          <div className="cl-form-grid">
            <Field label="Full name" error={errors.name}>
              <input
                autoComplete="name"
                value={form.name}
                onChange={(event) => update('name', event.target.value)}
                placeholder="Dana Brooks"
              />
            </Field>
            <Field label="Company (optional)">
              <input
                autoComplete="organization"
                value={form.company}
                onChange={(event) => update('company', event.target.value)}
                placeholder="Northstar Coverage"
              />
            </Field>
            <Field label="Work email" error={errors.email} className="full">
              <input
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={(event) => update('email', event.target.value)}
                placeholder="dana@agency.com"
              />
            </Field>
            <Field label="Mobile number" error={errors.mobile}>
              <input
                type="tel"
                autoComplete="tel"
                value={form.mobile}
                onChange={(event) => update('mobile', event.target.value)}
                placeholder="(212) 555-0142"
              />
            </Field>
            <Field label="Password" error={errors.password}>
              <input
                type="password"
                autoComplete="new-password"
                value={form.password}
                onChange={(event) => update('password', event.target.value)}
                placeholder="8 or more characters"
              />
            </Field>
          </div>
          <label className={`cl-check-row${errors.consent ? ' has-error' : ''}`}>
            <input
              type="checkbox"
              checked={form.consent}
              onChange={(event) => update('consent', event.target.checked)}
            />
            <span>I agree to the Terms of Service and Privacy Policy.</span>
          </label>
          {errors.consent && <p className="cl-form-error">{errors.consent}</p>}
          <button
            type="submit"
            className="cl-primary-button cl-full-button"
            disabled={status === 'loading'}
          >
            {status === 'loading' ? (
              <>
                <span className="cl-spinner" />
                Creating account...
              </>
            ) : (
              'Create account'
            )}
          </button>
        </form>
      </div>
    </section>
  );
}

function DemoActivationScreen({
  onNavigate,
}: {
  onNavigate: (screen: ClientLifecycleScreenId) => void;
}) {
  return (
    <>
      <ScreenHeading
        eyebrow="Demo workspace"
        title="Your sample operation is active"
        copy="Synthetic leads show how ACL Auto works: calls, conversations, bookings, and transfers before your account goes live."
        action={<StatusBadge tone="info">Synthetic data</StatusBadge>}
      />
      <section className="cl-live-band">
        <span className="cl-orb">A</span>
        <div>
          <strong>Ava is working sample leads</strong>
          <small>1 active conversation - 3 outcomes today</small>
        </div>
        <span className="cl-mini-wave">
          <i />
          <i />
          <i />
          <i />
          <i />
        </span>
        <StatusBadge tone="success">Sample active</StatusBadge>
      </section>
      <div className="cl-metric-grid">
        <div>
          <span>Sample leads</span>
          <strong>24</strong>
          <small>Today</small>
        </div>
        <div>
          <span>Contact rate</span>
          <strong>75%</strong>
          <small>18 contacted</small>
        </div>
        <div>
          <span>Appointments</span>
          <strong>4</strong>
          <small>Confirmed</small>
        </div>
        <div>
          <span>Live transfers</span>
          <strong>3</strong>
          <small>Connected</small>
        </div>
      </div>
      <div className="cl-two-column">
        <section className="cl-card">
          <div className="cl-section-heading">
            <div>
              <h2>Lead in motion</h2>
              <p>Jordan Ellis - Chicago, IL</p>
            </div>
            <StatusBadge tone="success">Booking</StatusBadge>
          </div>
          <div className="cl-demo-thread">
            <p className="lead">Could we do tomorrow instead?</p>
            <p className="ai">Absolutely. Dana has 11:30 AM or 2:00 PM.</p>
            <p className="lead">2 works for me.</p>
          </div>
          <div className="cl-event-row">
            <span>
              <Icon name="calendar" size={16} />
            </span>
            <div>
              <strong>Appointment being confirmed</strong>
              <small>Conversation and availability are attached to the sample lead.</small>
            </div>
          </div>
        </section>
        <section className="cl-card cl-activation-card">
          <span className="cl-eyebrow">Ready for your own buildout?</span>
          <h2>Move from sample activity to a managed account.</h2>
          <p>
            Your setup payment starts the buildout. Monthly service begins only when ACL marks your
            workspace Live.
          </p>
          <dl>
            <div>
              <dt>Due today</dt>
              <dd>$1,997</dd>
            </div>
            <div>
              <dt>Monthly when Live</dt>
              <dd>$1,497</dd>
            </div>
          </dl>
          <button
            type="button"
            className="cl-primary-button"
            onClick={() => onNavigate('checkout')}
          >
            Start my buildout
          </button>
          <button type="button" className="cl-text-button" onClick={() => onNavigate('support')}>
            Talk with support
          </button>
        </section>
      </div>
    </>
  );
}

function CheckoutScreen({ onNavigate }: { onNavigate: (screen: ClientLifecycleScreenId) => void }) {
  const [payment, setPayment] = useState<'idle' | 'processing' | 'declined' | 'success'>('idle');
  const [form, setForm] = useState({
    name: 'Dana Brooks',
    last4: '4242',
    postal: '10001',
    authorized: false,
  });
  const [error, setError] = useState('');
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!form.authorized) {
      setError('Authorize the recurring service terms to continue.');
      return;
    }
    if (!/^\d{4}$/.test(form.last4)) {
      setError('Enter four sample card digits.');
      return;
    }
    setError('');
    setPayment('processing');
    await delay(800);
    setPayment(form.last4 === '0002' ? 'declined' : 'success');
  };
  if (payment === 'success')
    return (
      <section className="cl-centered-state">
        <span className="cl-success-mark">
          <Icon name="check" size={25} />
        </span>
        <StatusBadge tone="success">Setup payment complete</StatusBadge>
        <h1>Your buildout is ready to begin</h1>
        <p>
          The $1,997 setup payment is recorded in this synthetic review. The $1,497 monthly service
          does not begin until the workspace goes Live.
        </p>
        <div className="cl-receipt">
          <div>
            <span>Receipt</span>
            <strong>ACL-SETUP-1042</strong>
          </div>
          <div>
            <span>Amount</span>
            <strong>$1,997</strong>
          </div>
          <div>
            <span>Monthly service</span>
            <strong>Begins when Live</strong>
          </div>
        </div>
        <button
          type="button"
          className="cl-primary-button"
          onClick={() => onNavigate('onboarding')}
        >
          Continue account setup
        </button>
      </section>
    );
  return (
    <>
      <ScreenHeading
        eyebrow="Activate service"
        title="Start your ACL Auto buildout"
        copy="Review the one-time setup payment and the recurring service authorization before continuing."
        action={<StatusBadge tone="neutral">Secure review</StatusBadge>}
      />
      <div className="cl-checkout-layout">
        <section className="cl-card cl-order-summary">
          <div className="cl-section-heading">
            <div>
              <h2>Order summary</h2>
              <p>Northstar Coverage</p>
            </div>
          </div>
          <div className="cl-price-row">
            <span>
              <strong>AI agent buildout and setup</strong>
              <small>One-time setup payment</small>
            </span>
            <b>$1,997</b>
          </div>
          <div className="cl-price-row monthly">
            <span>
              <strong>Managed AI agent service</strong>
              <small>Begins only when your workspace goes Live</small>
            </span>
            <b>
              $1,497<em>/month</em>
            </b>
          </div>
          <div className="cl-total-row">
            <span>Due today</span>
            <strong>$1,997</strong>
          </div>
          <InlineNotice tone="info" title="Monthly billing policy">
            No monthly service charge is due during onboarding or ACL review. The first $1,497
            service charge starts at activation.
          </InlineNotice>
        </section>
        <section className="cl-card">
          <div className="cl-section-heading">
            <div>
              <h2>Payment method</h2>
              <p>Synthetic review form - no payment is submitted</p>
            </div>
            <Icon name="shield" size={18} />
          </div>
          {payment === 'declined' && (
            <InlineNotice tone="danger" title="Payment was declined">
              Update the sample method and try again. Your demo remains available.
            </InlineNotice>
          )}
          <form onSubmit={submit}>
            <div className="cl-form-grid">
              <Field label="Name on card" className="full">
                <input
                  value={form.name}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, name: event.target.value }))
                  }
                />
              </Field>
              <Field label="Sample card ending" hint="Four digits only">
                <input
                  inputMode="numeric"
                  maxLength={4}
                  value={form.last4}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      last4: event.target.value.replace(/\D/g, ''),
                    }))
                  }
                />
              </Field>
              <Field label="Billing postal code">
                <input
                  inputMode="numeric"
                  value={form.postal}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, postal: event.target.value }))
                  }
                />
              </Field>
            </div>
            <label className={`cl-check-row${error ? ' has-error' : ''}`}>
              <input
                type="checkbox"
                checked={form.authorized}
                onChange={(event) =>
                  setForm((current) => ({ ...current, authorized: event.target.checked }))
                }
              />
              <span>
                I authorize the $1,997 setup payment and $1,497 monthly service beginning when my
                workspace goes Live.
              </span>
            </label>
            {error && <p className="cl-form-error">{error}</p>}
            <button
              type="submit"
              className="cl-primary-button cl-full-button"
              disabled={payment === 'processing'}
            >
              {payment === 'processing' ? (
                <>
                  <span className="cl-spinner" />
                  Processing...
                </>
              ) : payment === 'declined' ? (
                'Try another method'
              ) : (
                'Complete setup payment'
              )}
            </button>
            <button
              type="button"
              className="cl-text-button cl-full-button"
              onClick={() => onNavigate('demo-activation')}
            >
              Return to demo
            </button>
          </form>
        </section>
      </div>
    </>
  );
}

interface OnboardingDraft {
  agentName: string;
  company: string;
  timezone: string;
  licensedStates: string[];
  transferPhone: string;
  transferHours: string;
  bookingFallback: boolean;
  calendarConnected: boolean;
  availability: string[];
  leadAccess: boolean;
  consentEvidence: string;
  consentConfirmed: boolean;
  deliveryType: string;
  deliveryDestination: string;
  deliveryTested: boolean;
  vertical: string;
  businessSummary: string;
  qualifications: string;
  attested: boolean;
}

const INITIAL_ONBOARDING: OnboardingDraft = {
  agentName: 'Dana Brooks',
  company: 'Northstar Coverage',
  timezone: 'America/New_York',
  licensedStates: ['NY', 'NJ', 'PA'],
  transferPhone: '(212) 555-0142',
  transferHours: 'Mon-Fri, 9:00 AM-6:00 PM ET',
  bookingFallback: true,
  calendarConnected: false,
  availability: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
  leadAccess: false,
  consentEvidence: '',
  consentConfirmed: false,
  deliveryType: 'CRM',
  deliveryDestination: '',
  deliveryTested: false,
  vertical: 'Mortgage protection',
  businessSummary: '',
  qualifications: '',
  attested: false,
};

function OnboardingScreen({
  onNavigate,
}: {
  onNavigate: (screen: ClientLifecycleScreenId) => void;
}) {
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<OnboardingDraft>(INITIAL_ONBOARDING);
  const [saveState, setSaveState] = useState<'saved' | 'saving'>('saved');
  const [stepError, setStepError] = useState('');
  const [deliveryState, setDeliveryState] = useState<'idle' | 'testing' | 'success'>('idle');
  const saveTimer = useRef<number | null>(null);
  const update = <K extends keyof OnboardingDraft>(key: K, value: OnboardingDraft[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setSaveState('saving');
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => setSaveState('saved'), 550);
  };
  useEffect(
    () => () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    },
    [],
  );

  const validationMessages = () => [
    !draft.agentName.trim() || draft.licensedStates.length === 0
      ? 'Add the agent name and at least one licensed state.'
      : '',
    !draft.transferPhone.trim() || !draft.transferHours.trim()
      ? 'Add a transfer number and available hours.'
      : '',
    !draft.calendarConnected || draft.availability.length === 0
      ? 'Connect the calendar and select at least one available day.'
      : '',
    !draft.leadAccess || !draft.consentEvidence.trim() || !draft.consentConfirmed
      ? 'Complete lead access and consent evidence.'
      : '',
    !draft.deliveryDestination.trim() || !draft.deliveryTested
      ? 'Add a delivery destination and complete the local test.'
      : '',
    !draft.businessSummary.trim() || !draft.qualifications.trim()
      ? 'Add the business facts and qualification priorities.'
      : '',
    !draft.attested ? 'Confirm the setup summary before submitting.' : '',
  ];
  const validate = (index: number) => {
    const messages = validationMessages();
    setStepError(messages[index]);
    return !messages[index];
  };
  const next = () => {
    if (!validate(step)) return;
    setStepError('');
    setStep((current) => Math.min(ONBOARDING_STEPS.length - 1, current + 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const testDelivery = async () => {
    if (!draft.deliveryDestination.trim()) {
      setStepError('Add a destination before testing delivery.');
      return;
    }
    setDeliveryState('testing');
    await delay(650);
    setDeliveryState('success');
    update('deliveryTested', true);
  };
  const submitForReview = () => {
    const messages = validationMessages();
    const firstIncomplete = messages.slice(0, ONBOARDING_STEPS.length - 1).findIndex(Boolean);
    if (firstIncomplete >= 0) {
      setStep(firstIncomplete);
      setStepError(messages[firstIncomplete]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!validate(ONBOARDING_STEPS.length - 1)) return;
    onNavigate('awaiting-review');
  };

  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const stepContent = [
    <div className="cl-step-body" key="identity">
      <div className="cl-form-grid">
        <Field label="Agent name">
          <input
            value={draft.agentName}
            onChange={(event) => update('agentName', event.target.value)}
          />
        </Field>
        <Field label="Company (optional)">
          <input
            value={draft.company}
            onChange={(event) => update('company', event.target.value)}
          />
        </Field>
        <Field label="Account timezone" className="full">
          <select
            value={draft.timezone}
            onChange={(event) => update('timezone', event.target.value)}
          >
            {ACCOUNT_TIMEZONES.map((timezone) => (
              <option key={timezone}>{timezone}</option>
            ))}
          </select>
        </Field>
      </div>
      <fieldset className="cl-fieldset">
        <legend>Licensed states</legend>
        <div className="cl-state-picker">
          {US_STATES.map((state) => (
            <button
              key={state.code}
              type="button"
              aria-pressed={draft.licensedStates.includes(state.code)}
              className={draft.licensedStates.includes(state.code) ? 'selected' : ''}
              title={state.name}
              onClick={() =>
                update(
                  'licensedStates',
                  draft.licensedStates.includes(state.code)
                    ? draft.licensedStates.filter((code) => code !== state.code)
                    : [...draft.licensedStates, state.code],
                )
              }
            >
              {state.code}
            </button>
          ))}
        </div>
      </fieldset>
    </div>,
    <div className="cl-step-body" key="transfer">
      <div className="cl-form-grid">
        <Field label="Live transfer destination">
          <input
            type="tel"
            value={draft.transferPhone}
            onChange={(event) => update('transferPhone', event.target.value)}
          />
        </Field>
        <Field label="Transfer availability">
          <input
            value={draft.transferHours}
            onChange={(event) => update('transferHours', event.target.value)}
          />
        </Field>
      </div>
      <div className="cl-setting-row">
        <span>
          <strong>Appointment fallback</strong>
          <small>Offer the next available appointment when you cannot take a transfer.</small>
        </span>
        <Toggle
          checked={draft.bookingFallback}
          onChange={(value) => update('bookingFallback', value)}
          label="Appointment fallback"
        />
      </div>
      <InlineNotice tone="info" title="Managed routing">
        ACL confirms the final greeting and transfer test before launch.
      </InlineNotice>
    </div>,
    <div className="cl-step-body" key="calendar">
      <div className="cl-connection-row">
        <span>
          <Icon name="calendar" size={18} />
        </span>
        <div>
          <strong>Google Calendar</strong>
          <small>
            {draft.calendarConnected
              ? 'Connected to dana.brooks@example.com'
              : 'Calendar authorization is still required'}
          </small>
        </div>
        <button
          type="button"
          className={draft.calendarConnected ? 'cl-secondary-button' : 'cl-primary-button'}
          onClick={() => update('calendarConnected', !draft.calendarConnected)}
        >
          {draft.calendarConnected ? 'Disconnect' : 'Connect calendar'}
        </button>
      </div>
      <fieldset className="cl-fieldset">
        <legend>Days available for appointments</legend>
        <div className="cl-day-picker">
          {days.map((day) => (
            <button
              key={day}
              type="button"
              aria-pressed={draft.availability.includes(day)}
              className={draft.availability.includes(day) ? 'selected' : ''}
              onClick={() =>
                update(
                  'availability',
                  draft.availability.includes(day)
                    ? draft.availability.filter((value) => value !== day)
                    : [...draft.availability, day],
                )
              }
            >
              {day}
            </button>
          ))}
        </div>
      </fieldset>
      <div className="cl-form-grid">
        <Field label="Earliest appointment">
          <input type="time" defaultValue="09:00" />
        </Field>
        <Field label="Latest appointment">
          <input type="time" defaultValue="17:30" />
        </Field>
      </div>
    </div>,
    <div className="cl-step-body" key="source">
      <div className="cl-setting-row">
        <span>
          <strong>Lead source access</strong>
          <small>Allow ACL to receive the leads assigned to this workspace.</small>
        </span>
        <Toggle
          checked={draft.leadAccess}
          onChange={(value) => update('leadAccess', value)}
          label="Lead source access"
        />
      </div>
      <Field
        label="Consent evidence location"
        hint="Provide the page, form, or record where consent language can be reviewed."
      >
        <input
          value={draft.consentEvidence}
          onChange={(event) => update('consentEvidence', event.target.value)}
          placeholder="https://example.com/request-form"
        />
      </Field>
      <label className="cl-check-row">
        <input
          type="checkbox"
          checked={draft.consentConfirmed}
          onChange={(event) => update('consentConfirmed', event.target.checked)}
        />
        <span>
          I confirm the submitted lead sources retain disclosure text, timestamp, and form fields.
        </span>
      </label>
    </div>,
    <div className="cl-step-body" key="delivery">
      <div className="cl-form-grid">
        <Field label="Destination type">
          <select
            value={draft.deliveryType}
            onChange={(event) => update('deliveryType', event.target.value)}
          >
            <option>CRM</option>
            <option>Email</option>
            <option>Webhook</option>
          </select>
        </Field>
        <Field label="Destination">
          <input
            value={draft.deliveryDestination}
            onChange={(event) => {
              update('deliveryDestination', event.target.value);
              update('deliveryTested', false);
              setDeliveryState('idle');
            }}
            placeholder="Qualified lead pipeline"
          />
        </Field>
      </div>
      <div className="cl-action-row">
        <button
          type="button"
          className="cl-secondary-button"
          disabled={deliveryState === 'testing'}
          onClick={testDelivery}
        >
          {deliveryState === 'testing' ? (
            <>
              <span className="cl-spinner dark" />
              Testing...
            </>
          ) : (
            'Send local test'
          )}
        </button>
        {deliveryState === 'success' && <StatusBadge tone="success">Test received</StatusBadge>}
      </div>
      <InlineNotice
        tone={draft.deliveryTested ? 'success' : 'warning'}
        title={draft.deliveryTested ? 'Delivery test complete' : 'Delivery test pending'}
      >
        {draft.deliveryTested
          ? 'The synthetic destination acknowledged the sample qualified lead.'
          : 'A successful destination test is required before review.'}
      </InlineNotice>
    </div>,
    <div className="cl-step-body" key="business">
      <div className="cl-form-grid">
        <Field label="Primary market">
          <select
            value={draft.vertical}
            onChange={(event) => update('vertical', event.target.value)}
          >
            <option>Mortgage protection</option>
            <option>Final expense</option>
            <option>General life</option>
            <option>Health insurance</option>
          </select>
        </Field>
        <Field label="Agent display name">
          <input
            value={draft.agentName}
            onChange={(event) => update('agentName', event.target.value)}
          />
        </Field>
      </div>
      <Field
        label="Business facts"
        hint="Coverage focus, service area, and facts the AI should represent accurately."
      >
        <textarea
          rows={4}
          value={draft.businessSummary}
          onChange={(event) => update('businessSummary', event.target.value)}
          placeholder="We help homeowners compare mortgage protection options..."
        />
      </Field>
      <Field
        label="Qualification priorities"
        hint="What must be true before a transfer or booking is offered?"
      >
        <textarea
          rows={4}
          value={draft.qualifications}
          onChange={(event) => update('qualifications', event.target.value)}
          placeholder="Confirm homeowner status, state, timing, and interest..."
        />
      </Field>
    </div>,
    <div className="cl-step-body" key="review">
      <div className="cl-review-summary">
        <div>
          <span>Agent</span>
          <strong>{draft.agentName}</strong>
          <small>
            {draft.company || 'Independent agent'} -{' '}
            {draft.licensedStates.join(', ') || 'No states selected'}
          </small>
        </div>
        <div>
          <span>Transfers</span>
          <strong>{draft.transferPhone || 'Not provided'}</strong>
          <small>{draft.transferHours || 'No hours provided'}</small>
        </div>
        <div>
          <span>Calendar</span>
          <strong>{draft.calendarConnected ? 'Connected' : 'Not connected'}</strong>
          <small>{draft.availability.join(', ') || 'No availability selected'}</small>
        </div>
        <div>
          <span>Delivery</span>
          <strong>{draft.deliveryDestination || 'Not provided'}</strong>
          <small>{draft.deliveryTested ? 'Test received' : 'Test pending'}</small>
        </div>
      </div>
      <label className="cl-check-row">
        <input
          type="checkbox"
          checked={draft.attested}
          onChange={(event) => update('attested', event.target.checked)}
        />
        <span>
          I confirm these details are accurate and authorize ACL to review the workspace for launch.
        </span>
      </label>
      <InlineNotice tone="info" title="What happens next">
        ACL reviews routing, consent, calendar availability, and delivery before the workspace can
        go Live.
      </InlineNotice>
    </div>,
  ][step];

  return (
    <>
      <ScreenHeading
        eyebrow="Account setup"
        title={ONBOARDING_STEPS[step].label}
        copy={ONBOARDING_STEPS[step].summary}
        action={
          <span className="cl-save-state" data-saving={saveState === 'saving'}>
            <i />
            {saveState === 'saving' ? 'Saving...' : 'Saved locally'}
          </span>
        }
      />
      <div className="cl-onboarding-layout">
        <nav className="cl-step-nav" aria-label="Account setup steps">
          {ONBOARDING_STEPS.map((item, index) => (
            <button
              key={item.id}
              type="button"
              className={`${index === step ? 'active ' : ''}${index < step ? 'complete' : ''}`}
              aria-current={index === step ? 'step' : undefined}
              onClick={() => {
                setStep(index);
                setStepError('');
              }}
            >
              <span>{index < step ? <Icon name="check" size={11} /> : index + 1}</span>
              <div>
                <strong>{item.shortLabel}</strong>
                <small>{item.label}</small>
              </div>
            </button>
          ))}
        </nav>
        <section className="cl-card cl-step-card">
          {stepContent}
          {stepError && (
            <InlineNotice tone="danger" title="Complete this step">
              {stepError}
            </InlineNotice>
          )}
          <footer className="cl-step-footer">
            <button
              type="button"
              className="cl-secondary-button"
              onClick={() =>
                step === 0 ? onNavigate('demo-activation') : setStep((current) => current - 1)
              }
            >
              {step === 0 ? 'Save and exit' : 'Back'}
            </button>
            {step < ONBOARDING_STEPS.length - 1 ? (
              <button type="button" className="cl-primary-button" onClick={next}>
                Continue <Icon name="chevron" size={13} />
              </button>
            ) : (
              <button type="button" className="cl-primary-button" onClick={submitForReview}>
                Submit for ACL review
              </button>
            )}
          </footer>
        </section>
      </div>
    </>
  );
}

function AwaitingReviewScreen({
  onNavigate,
  notify,
  active,
}: {
  onNavigate: (screen: ClientLifecycleScreenId) => void;
  notify: (message: string) => void;
  active: boolean;
}) {
  const [tasks, setTasks] = useState(['calendar', 'greeting']);
  const [requestOpen, setRequestOpen] = useState(false);
  const [request, setRequest] = useState('');
  const [sent, setSent] = useState(false);
  const resolve = (id: string) => setTasks((current) => current.filter((task) => task !== id));
  return (
    <>
      <ScreenHeading
        eyebrow="Setup submitted"
        title="ACL is reviewing your workspace"
        copy="Your details are locked while the buildout is configured and tested. You can still resolve open items or request a change."
        action={<StatusBadge tone="warning">Review in progress</StatusBadge>}
      />
      <div className="cl-review-layout">
        <section className="cl-card">
          <div className="cl-section-heading">
            <div>
              <h2>Launch progress</h2>
              <p>Northstar Coverage</p>
            </div>
            <span className="cl-percent">50%</span>
          </div>
          <div className="cl-timeline">
            {REVIEW_TIMELINE.map((item) => (
              <div key={item.id} data-state={item.state}>
                <span>{item.state === 'complete' ? <Icon name="check" size={12} /> : null}</span>
                <div>
                  <strong>{item.label}</strong>
                  <small>{item.detail}</small>
                </div>
              </div>
            ))}
          </div>
        </section>
        <section className="cl-card">
          <div className="cl-section-heading">
            <div>
              <h2>Needs you</h2>
              <p>
                {tasks.length ? `${tasks.length} open items` : 'Everything requested is complete'}
              </p>
            </div>
            <StatusBadge tone={tasks.length ? 'warning' : 'success'}>
              {tasks.length || 'Clear'}
            </StatusBadge>
          </div>
          {tasks.length ? (
            <div className="cl-task-list">
              {tasks.includes('calendar') && (
                <div>
                  <span>
                    <strong>Reconnect calendar access</strong>
                    <small>Authorization ended before the test booking.</small>
                  </span>
                  <button
                    type="button"
                    className="cl-secondary-button"
                    onClick={() => {
                      resolve('calendar');
                      notify('Calendar access marked ready.');
                    }}
                  >
                    Resolve
                  </button>
                </div>
              )}
              {tasks.includes('greeting') && (
                <div>
                  <span>
                    <strong>Confirm transfer greeting</strong>
                    <small>Approve the agent introduction used for warm transfers.</small>
                  </span>
                  <button
                    type="button"
                    className="cl-secondary-button"
                    onClick={() => resolve('greeting')}
                  >
                    Confirm
                  </button>
                </div>
              )}
            </div>
          ) : (
            <InlineNotice tone="success" title="All requested items complete">
              ACL can continue the launch review without waiting on your account.
            </InlineNotice>
          )}
          <div className="cl-stacked-actions">
            <button
              type="button"
              className="cl-primary-button"
              onClick={() => setRequestOpen(true)}
            >
              Request a change
            </button>
            <button
              type="button"
              className="cl-secondary-button"
              onClick={() => onNavigate('onboarding')}
            >
              View submitted setup
            </button>
            <button
              type="button"
              className="cl-text-button"
              onClick={() => onNavigate('demo-activation')}
            >
              Open sample workspace
            </button>
          </div>
        </section>
      </div>
      {sent && (
        <InlineNotice tone="success" title="Change request received">
          ACL will review the request before the launch test continues.
        </InlineNotice>
      )}
      <ReviewDialog
        open={active && requestOpen}
        title="Request a setup change"
        description="The current review stays paused only if ACL needs new information."
        onClose={() => setRequestOpen(false)}
        footer={
          <>
            <button
              type="button"
              className="cl-secondary-button"
              onClick={() => setRequestOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="cl-primary-button"
              disabled={!request.trim()}
              onClick={() => {
                setRequestOpen(false);
                setSent(true);
                setRequest('');
              }}
            >
              Send request
            </button>
          </>
        }
      >
        <Field label="What should change?">
          <textarea
            rows={4}
            autoFocus
            value={request}
            onChange={(event) => setRequest(event.target.value)}
            placeholder="Update transfer hours, business details, or delivery instructions..."
          />
        </Field>
      </ReviewDialog>
    </>
  );
}

function PausedScreen({
  onNavigate,
  active,
}: {
  onNavigate: (screen: ClientLifecycleScreenId) => void;
  active: boolean;
}) {
  const [reason, setReason] = useState('ACL account review');
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [requested, setRequested] = useState(false);
  return (
    <>
      <ScreenHeading
        eyebrow="Workspace status"
        title="Managed outreach is paused"
        copy="Historical results remain available. New intake, calls, and messages are held until the pause is cleared."
        action={<StatusBadge tone="danger">Paused</StatusBadge>}
      />
      <section className="cl-issue-hero" data-tone="danger">
        <span>
          <Icon name="pause" size={22} />
        </span>
        <div>
          <strong>{reason}</strong>
          <p>Paused Jul 9 at 11:42 AM by ACL Operations. No lead activity is being generated.</p>
        </div>
        <select
          aria-label="Preview pause reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        >
          <option>ACL account review</option>
          <option>Client requested pause</option>
          <option>Payment issue</option>
          <option>Connection issue</option>
        </select>
      </section>
      <div className="cl-metric-grid">
        <div>
          <span>Active sessions</span>
          <strong>0</strong>
          <small>Held</small>
        </div>
        <div>
          <span>Leads worked</span>
          <strong>136</strong>
          <small>Historical</small>
        </div>
        <div>
          <span>Appointments</span>
          <strong>18</strong>
          <small>Historical</small>
        </div>
        <div>
          <span>Live transfers</span>
          <strong>11</strong>
          <small>Historical</small>
        </div>
      </div>
      <div className="cl-two-column">
        <section className="cl-card">
          <h2>What is paused</h2>
          <div className="cl-status-list">
            <div>
              <span>New lead intake</span>
              <StatusBadge tone="danger">Held</StatusBadge>
            </div>
            <div>
              <span>Calls and messages</span>
              <StatusBadge tone="danger">Held</StatusBadge>
            </div>
            <div>
              <span>Existing history</span>
              <StatusBadge tone="success">Available</StatusBadge>
            </div>
            <div>
              <span>Billing and account access</span>
              <StatusBadge tone="success">Available</StatusBadge>
            </div>
          </div>
        </section>
        <section className="cl-card">
          <h2>Resume path</h2>
          <p className="cl-section-copy">
            ACL reviews the reason and confirms safe routing before outreach resumes.
          </p>
          {requested ? (
            <InlineNotice tone="success" title="Resume requested">
              Your note is attached to the account review.
            </InlineNotice>
          ) : (
            <button type="button" className="cl-primary-button" onClick={() => setOpen(true)}>
              Request resume
            </button>
          )}
          <button
            type="button"
            className="cl-text-button"
            onClick={() =>
              onNavigate(
                reason === 'Payment issue'
                  ? 'payment-issue'
                  : reason === 'Connection issue'
                    ? 'connection-issue'
                    : 'support',
              )
            }
          >
            Open resolution details
          </button>
        </section>
      </div>
      <ReviewDialog
        open={active && open}
        title="Request outreach resume"
        description="ACL will verify the account before any calls or messages restart."
        onClose={() => setOpen(false)}
        footer={
          <>
            <button type="button" className="cl-secondary-button" onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="cl-primary-button"
              onClick={() => {
                setOpen(false);
                setRequested(true);
              }}
            >
              Send request
            </button>
          </>
        }
      >
        <Field label="Optional note">
          <textarea
            rows={3}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Add anything ACL should review..."
          />
        </Field>
      </ReviewDialog>
    </>
  );
}

function PaymentIssueScreen({
  onNavigate,
  active,
}: {
  onNavigate: (screen: ClientLifecycleScreenId) => void;
  active: boolean;
}) {
  const [dialog, setDialog] = useState(false);
  const [method, setMethod] = useState({ last4: '4242', postal: '10001' });
  const [state, setState] = useState<'past_due' | 'processing' | 'updated' | 'paid'>('past_due');
  const retry = async () => {
    setState('processing');
    await delay(750);
    setState('paid');
  };
  return (
    <>
      <ScreenHeading
        eyebrow="Billing alert"
        title={
          state === 'paid' ? 'Payment issue resolved' : 'Monthly service payment needs attention'
        }
        copy={
          state === 'paid'
            ? 'The account is current and managed outreach can continue.'
            : 'Your workspace remains available during the grace period. Update the payment method before service is paused.'
        }
        action={
          <StatusBadge tone={state === 'paid' ? 'success' : 'danger'}>
            {state === 'paid' ? 'Current' : 'Past due'}
          </StatusBadge>
        }
      />
      <section className="cl-issue-hero" data-tone={state === 'paid' ? 'success' : 'warning'}>
        <span>
          <Icon name={state === 'paid' ? 'check' : 'clock'} size={22} />
        </span>
        <div>
          <strong>{state === 'paid' ? '$1,497 payment received' : '$1,497 due Jul 8, 2026'}</strong>
          <p>
            {state === 'paid'
              ? 'Receipt INV-1008 is available in Billing.'
              : 'Grace period ends Jul 12 at 11:59 PM ET. Outreach is not paused yet.'}
          </p>
        </div>
      </section>
      {state === 'updated' && (
        <InlineNotice tone="success" title="Payment method updated">
          The sample method ending in {method.last4} is ready for retry.
        </InlineNotice>
      )}
      <div className="cl-two-column">
        <section className="cl-card">
          <h2>Payment method</h2>
          <dl className="cl-detail-list">
            <div>
              <dt>Method</dt>
              <dd>Card ending in {method.last4}</dd>
            </div>
            <div>
              <dt>Billing postal code</dt>
              <dd>{method.postal}</dd>
            </div>
            <div>
              <dt>Next retry</dt>
              <dd>{state === 'paid' ? 'Not needed' : 'Manual retry available'}</dd>
            </div>
          </dl>
          <button type="button" className="cl-secondary-button" onClick={() => setDialog(true)}>
            Update method
          </button>
        </section>
        <section className="cl-card">
          <h2>Service status</h2>
          <div className="cl-status-list">
            <div>
              <span>Workspace access</span>
              <StatusBadge tone="success">Available</StatusBadge>
            </div>
            <div>
              <span>Managed outreach</span>
              <StatusBadge tone={state === 'paid' ? 'success' : 'warning'}>
                {state === 'paid' ? 'Active' : 'Grace period'}
              </StatusBadge>
            </div>
            <div>
              <span>Payment</span>
              <StatusBadge tone={state === 'paid' ? 'success' : 'danger'}>
                {state === 'paid' ? 'Paid' : 'Past due'}
              </StatusBadge>
            </div>
          </div>
          <button
            type="button"
            className="cl-primary-button"
            disabled={state === 'processing' || state === 'paid'}
            onClick={retry}
          >
            {state === 'processing' ? (
              <>
                <span className="cl-spinner" />
                Retrying...
              </>
            ) : state === 'paid' ? (
              'Payment received'
            ) : (
              'Retry payment'
            )}
          </button>
          <button type="button" className="cl-text-button" onClick={() => onNavigate('billing')}>
            Open Billing
          </button>
        </section>
      </div>
      <ReviewDialog
        open={active && dialog}
        title="Update payment method"
        description="This synthetic form does not submit a real payment method."
        onClose={() => setDialog(false)}
        footer={
          <>
            <button type="button" className="cl-secondary-button" onClick={() => setDialog(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="cl-primary-button"
              disabled={!/^\d{4}$/.test(method.last4)}
              onClick={() => {
                setDialog(false);
                setState('updated');
              }}
            >
              Save method
            </button>
          </>
        }
      >
        <div className="cl-form-grid">
          <Field label="Sample card ending">
            <input
              inputMode="numeric"
              maxLength={4}
              value={method.last4}
              onChange={(event) =>
                setMethod((current) => ({
                  ...current,
                  last4: event.target.value.replace(/\D/g, ''),
                }))
              }
            />
          </Field>
          <Field label="Postal code">
            <input
              value={method.postal}
              onChange={(event) =>
                setMethod((current) => ({ ...current, postal: event.target.value }))
              }
            />
          </Field>
        </div>
      </ReviewDialog>
    </>
  );
}

function ConnectionIssueScreen() {
  const [state, setState] = useState<'degraded' | 'reconnecting' | 'healthy'>('degraded');
  const reconnect = async () => {
    setState('reconnecting');
    await delay(900);
    setState('healthy');
  };
  return (
    <>
      <ScreenHeading
        eyebrow="Connections"
        title={state === 'healthy' ? 'Connection restored' : 'Calendar connection is degraded'}
        copy={
          state === 'healthy'
            ? 'Availability checks and appointment booking are current.'
            : 'Lead work remains active. Appointment booking is using a managed fallback until calendar access is restored.'
        }
        action={
          <StatusBadge
            tone={state === 'healthy' ? 'success' : state === 'reconnecting' ? 'warning' : 'danger'}
          >
            {state === 'healthy'
              ? 'Healthy'
              : state === 'reconnecting'
                ? 'Reconnecting'
                : 'Degraded'}
          </StatusBadge>
        }
      />
      <section className="cl-issue-hero" data-tone={state === 'healthy' ? 'success' : 'warning'}>
        <span>
          <Icon name="connections" size={22} />
        </span>
        <div>
          <strong>
            {state === 'healthy'
              ? 'Google Calendar is connected'
              : 'Last successful calendar check: 42 minutes ago'}
          </strong>
          <p>
            {state === 'healthy'
              ? 'The next availability check completed just now.'
              : 'Existing appointments are unchanged. New booking requests remain in the managed fallback queue.'}
          </p>
        </div>
        {state !== 'healthy' && (
          <button
            type="button"
            className="cl-primary-button"
            disabled={state === 'reconnecting'}
            onClick={reconnect}
          >
            {state === 'reconnecting' ? (
              <>
                <span className="cl-spinner" />
                Reconnecting...
              </>
            ) : (
              'Reconnect'
            )}
          </button>
        )}
      </section>
      <section className="cl-card">
        <div className="cl-section-heading">
          <div>
            <h2>Managed connections</h2>
            <p>Current account capability status</p>
          </div>
        </div>
        <div className="cl-connection-list">
          <div>
            <span>
              <Icon name="send" size={16} />
            </span>
            <div>
              <strong>Lead intake</strong>
              <small>Receiving new leads</small>
            </div>
            <StatusBadge tone="success">Healthy</StatusBadge>
          </div>
          <div>
            <span>
              <Icon name="calendar" size={16} />
            </span>
            <div>
              <strong>Google Calendar</strong>
              <small>
                {state === 'healthy'
                  ? 'Availability current'
                  : state === 'reconnecting'
                    ? 'Authorization in progress'
                    : 'Authorization needs attention'}
              </small>
            </div>
            <StatusBadge tone={state === 'healthy' ? 'success' : 'warning'}>
              {state === 'healthy' ? 'Healthy' : 'Degraded'}
            </StatusBadge>
          </div>
          <div>
            <span>
              <Icon name="connections" size={16} />
            </span>
            <div>
              <strong>Lead delivery</strong>
              <small>Qualified lead handoff current</small>
            </div>
            <StatusBadge tone="success">Healthy</StatusBadge>
          </div>
        </div>
      </section>
    </>
  );
}

function OffboardedScreen({ active }: { active: boolean }) {
  const [exportState, setExportState] = useState<'idle' | 'preparing' | 'ready'>('idle');
  const [reactivate, setReactivate] = useState(false);
  const [requested, setRequested] = useState(false);
  const prepare = async () => {
    setExportState('preparing');
    await delay(850);
    setExportState('ready');
  };
  return (
    <>
      <ScreenHeading
        eyebrow="Account archive"
        title="Managed service ended Jul 1, 2026"
        copy="Historical outcomes and billing records remain available until the archive retention date."
        action={<StatusBadge tone="neutral">Offboarded</StatusBadge>}
      />
      <section className="cl-archive-band">
        <span>
          <Icon name="shield" size={20} />
        </span>
        <div>
          <strong>Archive access ends Sep 29, 2026</strong>
          <p>
            Request an export before this date. Live activity, managed routing, and account changes
            are unavailable.
          </p>
        </div>
      </section>
      <div className="cl-metric-grid">
        <div>
          <span>Leads worked</span>
          <strong>2,846</strong>
          <small>Lifetime</small>
        </div>
        <div>
          <span>Contacts</span>
          <strong>1,712</strong>
          <small>60.2%</small>
        </div>
        <div>
          <span>Appointments</span>
          <strong>184</strong>
          <small>Lifetime</small>
        </div>
        <div>
          <span>Transfers</span>
          <strong>96</strong>
          <small>Lifetime</small>
        </div>
      </div>
      <div className="cl-two-column">
        <section className="cl-card">
          <h2>Archive availability</h2>
          <div className="cl-status-list">
            <div>
              <span>Results and lead journeys</span>
              <StatusBadge tone="success">Read only</StatusBadge>
            </div>
            <div>
              <span>Invoices and receipts</span>
              <StatusBadge tone="success">Available</StatusBadge>
            </div>
            <div>
              <span>Recordings and transcripts</span>
              <StatusBadge tone="warning">Retention applies</StatusBadge>
            </div>
            <div>
              <span>Live activity</span>
              <StatusBadge tone="neutral">Unavailable</StatusBadge>
            </div>
          </div>
        </section>
        <section className="cl-card">
          <h2>Account actions</h2>
          {exportState === 'ready' ? (
            <InlineNotice tone="success" title="Export ready">
              The synthetic archive package is ready for secure retrieval.
            </InlineNotice>
          ) : (
            <p className="cl-section-copy">
              Prepare a managed export of eligible account records before the retention deadline.
            </p>
          )}
          <button
            type="button"
            className="cl-primary-button"
            disabled={exportState === 'preparing'}
            onClick={prepare}
          >
            {exportState === 'preparing' ? (
              <>
                <span className="cl-spinner" />
                Preparing...
              </>
            ) : exportState === 'ready' ? (
              'Retrieve export'
            ) : (
              'Request archive export'
            )}
          </button>
          {requested ? (
            <StatusBadge tone="success">Reactivation requested</StatusBadge>
          ) : (
            <button
              type="button"
              className="cl-secondary-button"
              onClick={() => setReactivate(true)}
            >
              Request reactivation
            </button>
          )}
        </section>
      </div>
      <ReviewDialog
        open={active && reactivate}
        title="Request account reactivation"
        description="ACL will review account eligibility and contact you before any service resumes."
        onClose={() => setReactivate(false)}
        footer={
          <>
            <button
              type="button"
              className="cl-secondary-button"
              onClick={() => setReactivate(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="cl-primary-button"
              onClick={() => {
                setReactivate(false);
                setRequested(true);
              }}
            >
              Send request
            </button>
          </>
        }
      />
    </>
  );
}

function AccountScreen({
  onNavigate,
  notify,
}: {
  onNavigate: (screen: ClientLifecycleScreenId) => void;
  notify: (message: string) => void;
}) {
  const [form, setForm] = useState({
    name: 'Dana Brooks',
    company: 'Northstar Coverage',
    email: 'dana.brooks@example.com',
    mobile: '(212) 555-0142',
    timezone: 'America/New_York',
  });
  const [saving, setSaving] = useState(false);
  const save = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    await delay(550);
    setSaving(false);
    notify('Account details saved.');
  };
  return (
    <>
      <ScreenHeading
        eyebrow="Profile"
        title="Account"
        copy="Update contact details for the person who owns this workspace."
        action={<StatusBadge tone="success">Workspace owner</StatusBadge>}
      />
      <div className="cl-two-column cl-profile-layout">
        <section className="cl-card">
          <form onSubmit={save}>
            <div className="cl-form-grid">
              <Field label="Full name">
                <input
                  value={form.name}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, name: event.target.value }))
                  }
                />
              </Field>
              <Field label="Company (optional)">
                <input
                  value={form.company}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, company: event.target.value }))
                  }
                />
              </Field>
              <Field label="Email">
                <input
                  type="email"
                  value={form.email}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, email: event.target.value }))
                  }
                />
              </Field>
              <Field label="Mobile">
                <input
                  type="tel"
                  value={form.mobile}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, mobile: event.target.value }))
                  }
                />
              </Field>
              <Field label="Timezone" className="full">
                <select
                  value={form.timezone}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, timezone: event.target.value }))
                  }
                >
                  {ACCOUNT_TIMEZONES.map((timezone) => (
                    <option key={timezone}>{timezone}</option>
                  ))}
                </select>
              </Field>
            </div>
            <button type="submit" className="cl-primary-button" disabled={saving}>
              {saving ? (
                <>
                  <span className="cl-spinner" />
                  Saving...
                </>
              ) : (
                'Save account'
              )}
            </button>
          </form>
        </section>
        <section className="cl-card">
          <h2>Managed account settings</h2>
          <div className="cl-managed-list">
            <div>
              <span>
                <strong>Licensed states</strong>
                <small>NY, NJ, PA</small>
              </span>
              <StatusBadge tone="neutral">Request only</StatusBadge>
            </div>
            <div>
              <span>
                <strong>Transfer routing</strong>
                <small>(212) 555-0142</small>
              </span>
              <StatusBadge tone="neutral">Request only</StatusBadge>
            </div>
            <div>
              <span>
                <strong>Qualification settings</strong>
                <small>Mortgage protection</small>
              </span>
              <StatusBadge tone="neutral">Request only</StatusBadge>
            </div>
          </div>
          <button
            type="button"
            className="cl-secondary-button"
            onClick={() => onNavigate('support')}
          >
            Request a change
          </button>
        </section>
      </div>
    </>
  );
}

function BillingScreen({ notify, active }: { notify: (message: string) => void; active: boolean }) {
  const [methodOpen, setMethodOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [method, setMethod] = useState('4242');
  const [cancelled, setCancelled] = useState(false);
  return (
    <>
      <ScreenHeading
        eyebrow="Profile"
        title="Billing"
        copy="Setup receipt, monthly service, payment method, and invoices."
        action={
          <StatusBadge tone={cancelled ? 'warning' : 'success'}>
            {cancelled ? 'Review requested' : 'Active'}
          </StatusBadge>
        }
      />
      <section className="cl-plan-band">
        <div>
          <span>Managed AI agent service</span>
          <strong>
            $1,497<small>/month</small>
          </strong>
          <p>Next charge Aug 8, 2026</p>
        </div>
        <div>
          <span>Payment method</span>
          <strong>Card ending in {method}</strong>
          <p>Billing postal code 10001</p>
        </div>
        <button type="button" className="cl-secondary-button" onClick={() => setMethodOpen(true)}>
          Update method
        </button>
      </section>
      <section className="cl-card">
        <div className="cl-section-heading">
          <div>
            <h2>Invoices</h2>
            <p>Account receipts and scheduled service</p>
          </div>
        </div>
        <div className="cl-invoice-table" role="region" aria-label="Billing invoices" tabIndex={0}>
          <div className="cl-invoice-head">
            <span>Invoice</span>
            <span>Date</span>
            <span>Description</span>
            <span>Amount</span>
            <span>Status</span>
          </div>
          {BILLING_INVOICES.map((invoice) => (
            <div className="cl-invoice-row" key={invoice.id}>
              <strong>{invoice.id}</strong>
              <span>{invoice.date}</span>
              <span>{invoice.description}</span>
              <b>{invoice.amount}</b>
              <StatusBadge tone={invoice.status === 'Paid' ? 'success' : 'info'}>
                {invoice.status}
              </StatusBadge>
            </div>
          ))}
        </div>
      </section>
      <section className="cl-danger-zone">
        <div>
          <strong>End managed service</strong>
          <p>
            Request a review of contract terms, timing, and archive retention before service
            changes.
          </p>
        </div>
        <button
          type="button"
          className="cl-danger-button"
          disabled={cancelled}
          onClick={() => setCancelOpen(true)}
        >
          {cancelled ? 'Cancellation review requested' : 'Request cancellation'}
        </button>
      </section>
      <ReviewDialog
        open={active && methodOpen}
        title="Update payment method"
        description="This synthetic form does not store a real payment method."
        onClose={() => setMethodOpen(false)}
        footer={
          <>
            <button
              type="button"
              className="cl-secondary-button"
              onClick={() => setMethodOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="cl-primary-button"
              disabled={!/^\d{4}$/.test(method)}
              onClick={() => {
                setMethodOpen(false);
                notify('Payment method updated.');
              }}
            >
              Save method
            </button>
          </>
        }
      >
        <Field label="Sample card ending">
          <input
            inputMode="numeric"
            maxLength={4}
            value={method}
            onChange={(event) => setMethod(event.target.value.replace(/\D/g, ''))}
          />
        </Field>
      </ReviewDialog>
      <ReviewDialog
        open={active && cancelOpen}
        title="Request service cancellation?"
        description="ACL will confirm contract terms and an effective date before service or billing changes. This request does not stop outreach."
        tone="danger"
        onClose={() => setCancelOpen(false)}
        footer={
          <>
            <button
              type="button"
              className="cl-secondary-button"
              onClick={() => setCancelOpen(false)}
            >
              Keep service
            </button>
            <button
              type="button"
              className="cl-danger-button"
              onClick={() => {
                setCancelOpen(false);
                setCancelled(true);
              }}
            >
              Send cancellation request
            </button>
          </>
        }
      />
    </>
  );
}

function NotificationsScreen({ notify }: { notify: (message: string) => void }) {
  const [values, setValues] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(NOTIFICATION_PREFERENCES.map((item) => [item.id, item.defaultEnabled])),
  );
  const [email, setEmail] = useState(true);
  const [sms, setSms] = useState(true);
  return (
    <>
      <ScreenHeading
        eyebrow="Profile"
        title="Notifications"
        copy="Choose how account outcomes and performance summaries reach you."
        action={<StatusBadge tone="success">Service alerts on</StatusBadge>}
      />
      <section className="cl-card">
        <div className="cl-section-heading">
          <div>
            <h2>Outcome alerts</h2>
            <p>Applied to Dana Brooks</p>
          </div>
        </div>
        <div className="cl-preference-list">
          {NOTIFICATION_PREFERENCES.map((item) => (
            <div key={item.id}>
              <span>
                <strong>{item.label}</strong>
                <small>{item.detail}</small>
                {item.required && <em>Required</em>}
              </span>
              <Toggle
                checked={values[item.id]}
                disabled={item.required}
                label={item.label}
                onChange={(checked) => setValues((current) => ({ ...current, [item.id]: checked }))}
              />
            </div>
          ))}
        </div>
      </section>
      <section className="cl-card">
        <div className="cl-section-heading">
          <div>
            <h2>Delivery channels</h2>
            <p>Security and service notices use every verified channel.</p>
          </div>
        </div>
        <div className="cl-preference-list">
          <div>
            <span>
              <strong>Email</strong>
              <small>dana.brooks@example.com</small>
            </span>
            <Toggle checked={email} onChange={setEmail} label="Email notifications" />
          </div>
          <div>
            <span>
              <strong>SMS</strong>
              <small>(212) 555-0142</small>
            </span>
            <Toggle checked={sms} onChange={setSms} label="SMS notifications" />
          </div>
        </div>
        <button
          type="button"
          className="cl-primary-button"
          onClick={() => notify('Notification preferences saved.')}
        >
          Save preferences
        </button>
      </section>
    </>
  );
}

function SupportScreen() {
  const [form, setForm] = useState({ category: 'Account setup', message: '' });
  const [state, setState] = useState<'form' | 'sending' | 'sent'>('form');
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!form.message.trim()) return;
    setState('sending');
    await delay(650);
    setState('sent');
  };
  if (state === 'sent')
    return (
      <section className="cl-centered-state">
        <span className="cl-success-mark">
          <Icon name="check" size={24} />
        </span>
        <StatusBadge tone="success">Request received</StatusBadge>
        <h1>ACL Support has your request</h1>
        <p>
          Reference ACL-2841 is attached to Northstar Coverage. A reply will go to
          dana.brooks@example.com.
        </p>
        <button
          type="button"
          className="cl-secondary-button"
          onClick={() => {
            setState('form');
            setForm((current) => ({ ...current, message: '' }));
          }}
        >
          Send another request
        </button>
      </section>
    );
  return (
    <>
      <ScreenHeading
        eyebrow="Profile"
        title="Support"
        copy="Send account, billing, or managed-operation requests to the ACL team."
        action={<StatusBadge tone="success">Support available</StatusBadge>}
      />
      <div className="cl-two-column cl-support-layout">
        <section className="cl-card">
          <form onSubmit={submit}>
            <Field label="Request type">
              <select
                value={form.category}
                onChange={(event) =>
                  setForm((current) => ({ ...current, category: event.target.value }))
                }
              >
                <option>Account setup</option>
                <option>Routing or hours</option>
                <option>Calendar or delivery</option>
                <option>Billing</option>
                <option>Account access</option>
                <option>Other</option>
              </select>
            </Field>
            <Field label="How can we help?">
              <textarea
                rows={7}
                value={form.message}
                onChange={(event) =>
                  setForm((current) => ({ ...current, message: event.target.value }))
                }
                placeholder="Include the lead, setting, or outcome ACL should review."
              />
            </Field>
            <button
              type="submit"
              className="cl-primary-button"
              disabled={!form.message.trim() || state === 'sending'}
            >
              {state === 'sending' ? (
                <>
                  <span className="cl-spinner" />
                  Sending...
                </>
              ) : (
                'Send support request'
              )}
            </button>
          </form>
        </section>
        <section className="cl-card">
          <h2>Account context</h2>
          <dl className="cl-detail-list">
            <div>
              <dt>Workspace</dt>
              <dd>Northstar Coverage</dd>
            </div>
            <div>
              <dt>Owner</dt>
              <dd>Dana Brooks</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>Live</dd>
            </div>
            <div>
              <dt>Support reference</dt>
              <dd>Included automatically</dd>
            </div>
          </dl>
          <InlineNotice tone="info" title="Urgent service issue">
            Payment and connection interruptions appear in the workspace as soon as ACL detects
            them.
          </InlineNotice>
        </section>
      </div>
    </>
  );
}

function SignoutScreen({
  onNavigate,
  active,
}: {
  onNavigate: (screen: ClientLifecycleScreenId) => void;
  active: boolean;
}) {
  const [signedOut, setSignedOut] = useState(false);
  if (signedOut)
    return (
      <section className="cl-centered-state">
        <span className="cl-success-mark">
          <Icon name="check" size={24} />
        </span>
        <h1>You are signed out</h1>
        <p>This synthetic review did not end a real session.</p>
        <button type="button" className="cl-primary-button" onClick={() => onNavigate('signup')}>
          Return to account access
        </button>
      </section>
    );
  return (
    <>
      <section className="cl-centered-state cl-muted-state">
        <Icon name="shield" size={26} />
        <h1>Account session</h1>
        <p>Confirm sign out to leave the client workspace.</p>
      </section>
      <ReviewDialog
        open={active}
        title="Sign out of ACL Auto?"
        description="You can sign back in to continue from this workspace."
        onClose={() => onNavigate('account')}
        footer={
          <>
            <button
              type="button"
              className="cl-secondary-button"
              onClick={() => onNavigate('account')}
            >
              Stay signed in
            </button>
            <button type="button" className="cl-danger-button" onClick={() => setSignedOut(true)}>
              Sign out
            </button>
          </>
        }
      />
    </>
  );
}

function SystemStatesScreen({
  onNavigate,
  active,
}: {
  onNavigate: (screen: ClientLifecycleScreenId) => void;
  active: boolean;
}) {
  const [retry, setRetry] = useState<'failed' | 'loading' | 'success'>('failed');
  const [reconnect, setReconnect] = useState<'reconnecting' | 'success'>('reconnecting');
  const [destructive, setDestructive] = useState(false);
  const [removed, setRemoved] = useState(false);
  const retryLoad = async () => {
    setRetry('loading');
    await delay(700);
    setRetry('success');
  };
  const reconnectNow = async () => {
    setReconnect('reconnecting');
    await delay(700);
    setReconnect('success');
  };
  return (
    <>
      <ScreenHeading
        eyebrow="UI review"
        title="Reusable system states"
        copy="Client-safe states for data availability, recovery, permissions, and confirmations."
        action={<StatusBadge tone="info">Synthetic</StatusBadge>}
      />
      <div className="cl-state-library">
        <section className="cl-card">
          <h2>Loading</h2>
          <div
            className="cl-skeleton"
            role="status"
            aria-busy="true"
            aria-label="Loading account summary"
          >
            <i />
            <i />
            <i />
          </div>
        </section>
        <section className="cl-card">
          <h2>Empty</h2>
          <div className="cl-empty-state">
            <span>
              <Icon name="calendar" size={19} />
            </span>
            <strong>No appointments yet</strong>
            <p>Qualified leads will appear after a time is confirmed.</p>
            <button
              type="button"
              className="cl-secondary-button"
              onClick={() => onNavigate('onboarding')}
            >
              Review availability
            </button>
          </div>
        </section>
        <section className="cl-card">
          <h2>Partial data</h2>
          <InlineNotice tone="warning" title="Two results are still processing">
            Confirmed outcomes are available below. Pending transcripts will appear when processing
            completes.
          </InlineNotice>
          <div className="cl-compact-metrics">
            <span>
              <b>18</b> confirmed
            </span>
            <span>
              <b>2</b> processing
            </span>
          </div>
        </section>
        <section className="cl-card">
          <h2>Stale</h2>
          <InlineNotice tone="warning" title="Last updated 18 minutes ago">
            Live indicators are paused while the latest account snapshot is delayed.
          </InlineNotice>
          <button type="button" className="cl-secondary-button" onClick={reconnectNow}>
            Refresh status
          </button>
        </section>
        <section className="cl-card">
          <h2>Failed and retry</h2>
          {retry === 'failed' ? (
            <InlineNotice tone="danger" title="Results could not load">
              Existing filters are preserved. Retry this section without leaving the page.
            </InlineNotice>
          ) : retry === 'loading' ? (
            <div className="cl-inline-loading">
              <span className="cl-spinner dark" />
              Retrying results...
            </div>
          ) : (
            <InlineNotice tone="success" title="Results restored">
              The latest confirmed outcomes are available.
            </InlineNotice>
          )}
          <button
            type="button"
            className="cl-secondary-button"
            disabled={retry === 'loading'}
            onClick={retryLoad}
          >
            Retry
          </button>
        </section>
        <section className="cl-card">
          <h2>Reconnecting</h2>
          {reconnect === 'reconnecting' ? (
            <div className="cl-reconnect-row">
              <span className="cl-spinner dark" />
              <span>
                <strong>Reconnecting live activity</strong>
                <small>Last good update retained</small>
              </span>
            </div>
          ) : (
            <InlineNotice tone="success" title="Connection restored">
              Live account updates are current.
            </InlineNotice>
          )}
          <button type="button" className="cl-secondary-button" onClick={reconnectNow}>
            Retry now
          </button>
        </section>
        <section className="cl-card">
          <h2>Permission denied</h2>
          <div className="cl-empty-state">
            <span>
              <Icon name="shield" size={19} />
            </span>
            <strong>Owner permission required</strong>
            <p>This account does not expose recordings or downloads to your role.</p>
            <button type="button" className="cl-secondary-button">
              Request access
            </button>
          </div>
        </section>
        <section className="cl-card">
          <h2>Success</h2>
          <InlineNotice tone="success" title="Change request sent">
            ACL-2841 is now attached to the account.
          </InlineNotice>
        </section>
        <section className="cl-card">
          <h2>Destructive confirmation</h2>
          {removed ? (
            <InlineNotice tone="success" title="Connection removed">
              Managed outreach remains held until a replacement is approved.
            </InlineNotice>
          ) : (
            <>
              <p className="cl-section-copy">Removing a connection can stop appointment booking.</p>
              <button
                type="button"
                className="cl-danger-button"
                onClick={() => setDestructive(true)}
              >
                Remove connection
              </button>
            </>
          )}
        </section>
      </div>
      <ReviewDialog
        open={active && destructive}
        title="Remove this connection?"
        description="Appointment booking will be held until ACL approves a replacement connection."
        tone="danger"
        onClose={() => setDestructive(false)}
        footer={
          <>
            <button
              type="button"
              className="cl-secondary-button"
              onClick={() => setDestructive(false)}
            >
              Keep connection
            </button>
            <button
              type="button"
              className="cl-danger-button"
              onClick={() => {
                setDestructive(false);
                setRemoved(true);
              }}
            >
              Remove connection
            </button>
          </>
        }
      />
    </>
  );
}

function ScreenContent({
  screen,
  onNavigate,
  notify,
  active,
}: {
  screen: ClientLifecycleScreenId;
  onNavigate: (screen: ClientLifecycleScreenId) => void;
  notify: (message: string) => void;
  active: boolean;
}) {
  switch (screen) {
    case 'signup':
      return <SignupScreen onNavigate={onNavigate} notify={notify} />;
    case 'demo-activation':
      return <DemoActivationScreen onNavigate={onNavigate} />;
    case 'checkout':
      return <CheckoutScreen onNavigate={onNavigate} />;
    case 'onboarding':
      return <OnboardingScreen onNavigate={onNavigate} />;
    case 'awaiting-review':
      return <AwaitingReviewScreen onNavigate={onNavigate} notify={notify} active={active} />;
    case 'paused':
      return <PausedScreen onNavigate={onNavigate} active={active} />;
    case 'payment-issue':
      return <PaymentIssueScreen onNavigate={onNavigate} active={active} />;
    case 'connection-issue':
      return <ConnectionIssueScreen />;
    case 'offboarded':
      return <OffboardedScreen active={active} />;
    case 'account':
      return <AccountScreen onNavigate={onNavigate} notify={notify} />;
    case 'billing':
      return <BillingScreen notify={notify} active={active} />;
    case 'notifications':
      return <NotificationsScreen notify={notify} />;
    case 'support':
      return <SupportScreen />;
    case 'signout':
      return <SignoutScreen onNavigate={onNavigate} active={active} />;
    case 'system-states':
      return <SystemStatesScreen onNavigate={onNavigate} active={active} />;
    default:
      return <DemoActivationScreen onNavigate={onNavigate} />;
  }
}

export default function ClientLifecycleScreens({
  screen,
  onNavigate,
}: ClientLifecycleScreensProps) {
  const [notice, setNotice] = useState('');
  const noticeTimer = useRef<number | null>(null);
  const activeScreenRef = useRef(screen);
  activeScreenRef.current = screen;
  const notify = (message: string) => {
    setNotice(message);
    if (noticeTimer.current) window.clearTimeout(noticeTimer.current);
    noticeTimer.current = window.setTimeout(() => setNotice(''), 3200);
  };
  useEffect(
    () => () => {
      if (noticeTimer.current) window.clearTimeout(noticeTimer.current);
    },
    [],
  );
  useEffect(() => {
    setNotice('');
    if (noticeTimer.current) window.clearTimeout(noticeTimer.current);
    noticeTimer.current = null;
  }, [screen]);
  return (
    <LifecycleShell
      screen={screen}
      onNavigate={onNavigate}
      notice={
        notice ? (
          <div className="cl-toast" role="status">
            <Icon name="check" size={14} />
            <span>{notice}</span>
            <button
              type="button"
              aria-label="Dismiss notification"
              title="Dismiss"
              onClick={() => setNotice('')}
            >
              x
            </button>
          </div>
        ) : null
      }
    >
      {CLIENT_LIFECYCLE_SCREENS.map((item) => {
        const active = item.id === screen;
        return (
          <div
            key={item.id}
            className={`cl-screen cl-screen-${item.id}`}
            data-screen-label={item.label}
            hidden={!active}
            aria-hidden={active ? undefined : 'true'}
            {...(active ? {} : { inert: '' })}
          >
            <ScreenContent
              screen={item.id}
              onNavigate={onNavigate}
              active={active}
              notify={(message) => {
                if (activeScreenRef.current === item.id) notify(message);
              }}
            />
          </div>
        );
      })}
    </LifecycleShell>
  );
}
